import { useState, useEffect, useRef } from "react";
import {
  Drawer, Input, InputNumber, Select, Switch, Checkbox, Tooltip, Button,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, HolderOutlined, ArrowUpOutlined,
  ArrowDownOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  createSmartSearchPlan, updateSmartSearchPlan, fetchSmartSearchMeta,
} from "../services/smartSearchService";
import {
  UNLIMITED, BRAND, TIERS, toOptions, priceModeLabel,
  included as isIncluded,
} from "./planShared";

/**
 * Create or edit one Smart Search plan.
 *
 * The form is sectioned rather than one long column because the fields fall into
 * groups a reader already thinks in — what it is, what it allows, what it costs,
 * and what the pricing card says. A flat list of eleven inputs hides that.
 */

const sectionStyle = {
  background: "#fff", border: "1px solid #eef0f6", borderRadius: 14,
  padding: "16px 18px", marginBottom: 14,
};

const Section = ({ title, hint, children }) => (
  <div style={sectionStyle}>
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</div>
      {hint && <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>{hint}</div>}
    </div>
    {children}
  </div>
);

const Field = ({ label, error, hint, children, tip }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{
      display: "flex", alignItems: "center", gap: 6, fontSize: 12,
      fontWeight: 600, color: "#374151", marginBottom: 6,
    }}>
      {label}
      {tip && (
        <Tooltip title={tip}>
          <InfoCircleOutlined style={{ color: "#94a3b8", fontSize: 12 }} />
        </Tooltip>
      )}
    </label>
    {children}
    {error
      ? <div style={{ fontSize: 11.5, color: "#dc2626", marginTop: 5 }}>{error}</div>
      : hint && <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 5 }}>{hint}</div>}
  </div>
);

/**
 * A number that can also mean "no ceiling".
 *
 * The API carries no-ceiling as -1, but -1 is not a quota anyone would type or
 * want to read, so the checkbox is the interface and -1 never appears on screen.
 * The last typed number is kept while Unlimited is on, so ticking it by mistake
 * does not lose what was there.
 */
const LimitInput = ({ value, onChange, suffix, placeholder, disabled }) => {
  const unlimited = Number(value) === UNLIMITED;
  const lastFinite = useRef(unlimited ? null : value);
  if (!unlimited && value !== null && value !== undefined) lastFinite.current = value;

  return (
    <div>
      <InputNumber
        value={unlimited ? null : value}
        onChange={onChange}
        disabled={disabled || unlimited}
        min={0}
        style={{ width: "100%" }}
        placeholder={unlimited ? "Unlimited" : placeholder}
        addonAfter={suffix}
      />
      <Checkbox
        checked={unlimited}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked ? UNLIMITED : (lastFinite.current ?? null))}
        style={{ marginTop: 8, fontSize: 12.5 }}
      >
        Unlimited
      </Checkbox>
    </div>
  );
};

/**
 * The feature list that appears on the pricing card.
 *
 * Stored under `features.bullets` and called that in the payload, but never on
 * screen: "bullets" is how the JSON is shaped, and the person filling this in is
 * writing features.
 *
 * Order is content here — it is the order they appear on the card — so rows can
 * be dragged. The arrows are not decoration next to that: a drag is impossible
 * from a keyboard, and this list is the one part of the form where sequence
 * matters.
 */
const FeatureRows = ({ bullets, onChange, disabled }) => {
  const dragFrom = useRef(null);

  const move = (from, to) => {
    if (to < 0 || to >= bullets.length || from === to) return;
    const next = [...bullets];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    onChange(next);
  };

  const setText = (i, text) => onChange(bullets.map((b, j) => (j === i ? { ...b, text } : b)));
  const setIncluded = (i, v) => onChange(bullets.map((b, j) => (j === i ? { ...b, included: v } : b)));
  const remove = (i) => onChange(bullets.filter((_, j) => j !== i));

  return (
    <div>
      {bullets.map((b, i) => (
        <div
          key={i}
          draggable={!disabled}
          onDragStart={() => { dragFrom.current = i; }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => { move(dragFrom.current, i); dragFrom.current = null; }}
          style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
            background: "#fafbff", border: "1px solid #eef0f6", borderRadius: 10,
            padding: "6px 8px",
          }}
        >
          <HolderOutlined style={{ color: "#cbd5e1", cursor: disabled ? "default" : "grab", fontSize: 14 }} />
          <Input
            value={b.text}
            onChange={(e) => setText(i, e.target.value)}
            disabled={disabled}
            placeholder="e.g. Unlimited identical-mark searches"
            variant="borderless"
            style={{
              flex: 1, fontSize: 12.5,
              /* An excluded line is still copy on the card — it reads as
                 "you do not get this" — so it is struck through rather than
                 dimmed into looking disabled. */
              ...(isIncluded(b)
                ? {}
                : { textDecoration: "line-through", color: "#a0aab8" }),
            }}
          />
          <Tooltip title={isIncluded(b) ? "Included in this plan" : "Shown as not included"}>
            <Switch
              size="small"
              checked={isIncluded(b)}
              disabled={disabled}
              onChange={(v) => setIncluded(i, v)}
              style={isIncluded(b) ? { background: BRAND } : undefined}
            />
          </Tooltip>
          <div style={{ display: "inline-flex", gap: 2 }}>
            <Button size="small" type="text" disabled={disabled || i === 0}
              icon={<ArrowUpOutlined style={{ fontSize: 11 }} />} onClick={() => move(i, i - 1)} />
            <Button size="small" type="text" disabled={disabled || i === bullets.length - 1}
              icon={<ArrowDownOutlined style={{ fontSize: 11 }} />} onClick={() => move(i, i + 1)} />
            <Button size="small" type="text" danger disabled={disabled}
              icon={<DeleteOutlined style={{ fontSize: 12 }} />} onClick={() => remove(i)} />
          </div>
        </div>
      ))}

      <Button
        type="dashed" block disabled={disabled} icon={<PlusOutlined />}
        onClick={() => onChange([...bullets, { text: "", included: true }])}
        style={{ fontSize: 12.5, height: 34 }}
      >
        Add a feature
      </Button>
    </div>
  );
};

const EMPTY = {
  plan_code: "",
  display_name: "",
  tier: "pro",
  monthly_quota: null,
  watch_brands_limit: null,
  monthly_price_inr: null,
  yearly_price_inr: null,
  features: { cta: "", badge: "", price_mode: "paid", bullets: [] },
  is_active: 1,
};

/** Only what the form owns, so a stray field from the list response is not echoed back. */
const toPayload = (f) => ({
  plan_code: f.plan_code?.trim(),
  display_name: f.display_name?.trim(),
  tier: f.tier,
  monthly_quota: f.monthly_quota,
  watch_brands_limit: f.watch_brands_limit,
  monthly_price_inr: f.monthly_price_inr,
  /* null is the answer, not a missing field: it is how "not sold yearly" is
     stored, so it has to survive the payload rather than being stripped. */
  yearly_price_inr: f.yearly_price_inr,
  features: {
    /* Whatever else the features object carries — `price_mode`, and anything
       the backend adds later — is carried straight back. Rebuilding this object
       from only the fields this form knows about would silently delete the
       rest on every edit. */
    ...f.features,
    cta: f.features.cta?.trim() || "",
    badge: f.features.badge?.trim() || "",
    // A row someone added and left blank is not copy; it would render as an
    // empty tick on the card.
    bullets: (f.features.bullets || [])
      .map((b) => ({ text: (b.text || "").trim(), included: isIncluded(b) }))
      .filter((b) => b.text),
  },
  is_active: f.is_active ? 1 : 0,
});

export default function PlanFormDrawer({ open, plan, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);

  /* Asked for on first open rather than on mount: the drawer spends most of its
     life closed, and the call is cached, so every open after this is free. */
  useEffect(() => {
    if (!open || meta) return undefined;
    let alive = true;
    fetchSmartSearchMeta().then((m) => { if (alive) setMeta(m || {}); });
    return () => { alive = false; };
  }, [open, meta]);

  const editing = Boolean(plan?.id);
  const subscribed = Number(plan?.active_subscriptions || 0);
  /* A code customers are already subscribed under is an identifier other systems
     hold; renaming it would orphan them. The name is free to change. */
  const codeLocked = editing && subscribed > 0;

  /* Re-seeded on every open. The drawer instance is reused across rows, and a
     previous plan's copy left in the fields is how the wrong plan gets edited. */
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (!plan) { setForm(EMPTY); return; }
    const f = plan.features || {};
    setForm({
      plan_code: plan.plan_code || "",
      display_name: plan.display_name || "",
      tier: plan.tier || "pro",
      monthly_quota: plan.monthly_quota ?? null,
      watch_brands_limit: plan.watch_brands_limit ?? null,
      monthly_price_inr: plan.monthly_price_inr ?? null,
      yearly_price_inr: plan.yearly_price_inr ?? null,
      features: {
        ...f,
        cta: f.cta || "",
        badge: f.badge || "",
        bullets: Array.isArray(f.bullets)
          // Older rows may hold plain strings rather than {text, included}.
          ? f.bullets.map((b) => (typeof b === "string"
            ? { text: b, included: true }
            : { text: b?.text || "", included: isIncluded(b) }))
          : [],
      },
      is_active: plan.is_active === 1 || plan.is_active === true ? 1 : 0,
    });
  }, [open, plan]);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => (p[k] ? { ...p, [k]: undefined } : p));
  };
  const setFeature = (k, v) => setForm((p) => ({ ...p, features: { ...p.features, [k]: v } }));

  const notSoldYearly = form.yearly_price_inr === null || form.yearly_price_inr === undefined;
  const priceMode = String(form.features.price_mode || "").toLowerCase();

  const submit = async () => {
    if (saving) return;

    /* Checked here as well as on the server: these three are what the card
       cannot render without, and a round trip to be told so is a round trip
       wasted. Everything else is the server's to judge. */
    const local = {};
    if (!form.display_name?.trim()) local.display_name = "A display name is required.";
    if (!form.plan_code?.trim()) local.plan_code = "A plan code is required.";
    if (form.monthly_price_inr === null || form.monthly_price_inr === undefined) {
      local.monthly_price_inr = "A monthly price is required.";
    }
    if (Object.keys(local).length) {
      setErrors(local);
      return;
    }

    setSaving(true);
    try {
      const body = toPayload(form);
      const res = editing
        ? await updateSmartSearchPlan(plan.id, body)
        : await createSmartSearchPlan(body);
      toast.success(res?.msg || res?.message || (editing ? "Plan updated." : "Plan created."));
      onSaved();
      onClose();
    } catch (err) {
      /* Field errors belong beside their field — a toast listing five of them
         makes the reader hunt for which input is wrong. */
      if (err.status === 422 && err.errors) {
        const flat = {};
        Object.entries(err.errors).forEach(([k, v]) => {
          flat[k.replace(/^features\./, "")] = Array.isArray(v) ? v[0] : String(v);
        });
        setErrors(flat);
        toast.error("Please correct the highlighted fields.");
      } else {
        toast.error(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={saving ? undefined : onClose}
      width={620}
      maskClosable={!saving}
      closable={!saving}
      styles={{
        body: { padding: 18, background: "#f7f8fc" },
        header: { background: `linear-gradient(135deg, ${BRAND}, #7c3aed)`, borderBottom: "none" },
      }}
      title={(
        <div>
          <div style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>
            {editing ? "Edit plan" : "New Smart Search plan"}
          </div>
          <div style={{ color: "rgba(255,255,255,0.78)", fontSize: 11.5, marginTop: 2 }}>
            {editing
              ? `${plan.display_name}${subscribed ? ` · ${subscribed} active subscription${subscribed === 1 ? "" : "s"}` : ""}`
              : "This becomes a card on the pricing page"}
          </div>
        </div>
      )}
      footer={(
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "4px 0" }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            type="primary" loading={saving} onClick={submit}
            style={{ background: BRAND, borderColor: BRAND, fontWeight: 600 }}
          >
            {editing ? "Save changes" : "Create plan"}
          </Button>
        </div>
      )}
    >
      <Section title="Basics" hint="What this plan is called, and how the system refers to it">
        <Field label="Display name" error={errors.display_name}>
          <Input
            value={form.display_name}
            onChange={(e) => set("display_name", e.target.value)}
            disabled={saving}
            placeholder="e.g. Pro"
            status={errors.display_name ? "error" : ""}
          />
        </Field>

        <Field
          label="Plan code"
          error={errors.plan_code}
          hint={codeLocked ? undefined : "Lowercase, no spaces — used by the API and in payment records."}
          tip={codeLocked
            ? `Locked: ${subscribed} customer${subscribed === 1 ? " is" : "s are"} subscribed under this code. Changing it would break their records — create a new plan instead.`
            : undefined}
        >
          <Input
            value={form.plan_code}
            onChange={(e) => set("plan_code", e.target.value)}
            disabled={saving || codeLocked}
            placeholder="e.g. ss_pro"
            status={errors.plan_code ? "error" : ""}
          />
        </Field>

        <Field label="Tier" error={errors.tier} hint="Pick one, or type your own.">
          {/* Offered from `/plans/meta`, with the known list as a fallback while
              it loads or if it cannot be reached. `tags` mode so a tier nobody
              has listed yet can still be typed; it works in arrays, and only the
              last pick is kept, so the field still holds one tier. */}
          <Select
            mode="tags"
            maxCount={1}
            value={form.tier ? [form.tier] : []}
            onChange={(vals) => set("tier", vals[vals.length - 1] || "")}
            disabled={saving}
            style={{ width: "100%" }}
            placeholder="Select or type a tier"
            options={toOptions(meta?.tiers?.length ? meta.tiers : TIERS, { current: form.tier })}
            status={errors.tier ? "error" : ""}
          />
        </Field>
      </Section>

      <Section title="Limits" hint="What the plan allows each month">
        <Field label="Monthly search quota" error={errors.monthly_quota}>
          <LimitInput
            value={form.monthly_quota}
            onChange={(v) => set("monthly_quota", v)}
            suffix="/mo"
            placeholder="e.g. 500"
            disabled={saving}
          />
        </Field>
        <Field label="Brands that can be watched" error={errors.watch_brands_limit}>
          <LimitInput
            value={form.watch_brands_limit}
            onChange={(v) => set("watch_brands_limit", v)}
            suffix="brands"
            placeholder="e.g. 10"
            disabled={saving}
          />
        </Field>
      </Section>

      <Section title="Pricing" hint="Shown on the card exactly as entered">
        {/* The options come from the database, so a mode added there appears
            here without a release. Known codes get readable words; one this
            build has never heard of shows as its raw value rather than being
            hidden, which would make it un-pickable. */}
        <Field
          label="Price mode"
          error={errors.price_mode}
          hint={priceMode === "contact"
            ? "The card shows “Contact sales” instead of an amount."
            : priceMode === "free"
              ? "The card shows “Free” instead of an amount."
              : "The card shows the monthly amount below."}
        >
          <Select
            value={priceMode || undefined}
            onChange={(v) => setFeature("price_mode", v)}
            disabled={saving}
            loading={!meta}
            style={{ width: "100%" }}
            placeholder="Select a price mode"
            options={toOptions(
              meta?.price_modes?.length ? meta.price_modes : ["paid", "free", "contact"],
              { label: priceModeLabel, current: priceMode },
            )}
            status={errors.price_mode ? "error" : ""}
          />
        </Field>

        <Field label="Monthly price" error={errors.monthly_price_inr}>
          <InputNumber
            value={form.monthly_price_inr}
            onChange={(v) => set("monthly_price_inr", v)}
            disabled={saving}
            min={0}
            style={{ width: "100%" }}
            prefix="₹"
            addonAfter="/mo"
            status={errors.monthly_price_inr ? "error" : ""}
          />
        </Field>

        <Field label="Yearly price" error={errors.yearly_price_inr}>
          <InputNumber
            value={notSoldYearly ? null : form.yearly_price_inr}
            onChange={(v) => set("yearly_price_inr", v)}
            disabled={saving || notSoldYearly}
            min={0}
            style={{ width: "100%" }}
            prefix="₹"
            addonAfter="/yr"
            placeholder={notSoldYearly ? "Not sold yearly" : "e.g. 9990"}
          />
          <Checkbox
            checked={notSoldYearly}
            disabled={saving}
            onChange={(e) => set("yearly_price_inr", e.target.checked ? null : 0)}
            style={{ marginTop: 8, fontSize: 12.5 }}
          >
            Not sold yearly
          </Checkbox>
        </Field>
      </Section>

      <Section title="Card copy" hint="The words a customer reads on the pricing page">
        <Field label="Badge" error={errors.badge} hint="Leave empty for no ribbon.">
          <Input
            value={form.features.badge}
            onChange={(e) => setFeature("badge", e.target.value)}
            disabled={saving}
            placeholder="e.g. RECOMMENDED"
            status={errors.badge ? "error" : ""}
          />
        </Field>

        <Field label="Call to action" error={errors.cta}>
          <Input
            value={form.features.cta}
            onChange={(e) => setFeature("cta", e.target.value)}
            disabled={saving}
            placeholder="e.g. Best for growing practices"
            status={errors.cta ? "error" : ""}
          />
        </Field>

        <Field
          label="Features"
          error={errors.bullets}
          hint="Switch a row off to show it on the card as not included. Drag a row, or use the arrows, to reorder."
        >
          <FeatureRows
            bullets={form.features.bullets}
            onChange={(b) => setFeature("bullets", b)}
            disabled={saving}
          />
        </Field>
      </Section>

      <Section title="Status">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Switch
            checked={form.is_active === 1}
            onChange={(v) => set("is_active", v ? 1 : 0)}
            disabled={saving}
            style={form.is_active === 1 ? { background: BRAND } : undefined}
          />
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}>
              {form.is_active === 1 ? "Active" : "Inactive"}
            </div>
            <div style={{ fontSize: 11.5, color: "#94a3b8" }}>
              {form.is_active === 1
                ? "Customers can subscribe to this plan."
                : "Hidden from the pricing page. Existing subscriptions are unaffected."}
            </div>
          </div>
        </div>
      </Section>
    </Drawer>
  );
}
